import { useCallback } from 'react';

import { useForm } from 'react-hook-form';
import { useRecoilState } from 'recoil';

import type {
  LocationControlItem,
  LocationEditableFields,
  LocationForm as LocationFormType,
} from '../../../../../state/locationsForm';
import {
  locationsControlState,
  isEqualLocations,
  TypeLocation,
  DEFAULT_LOCATION,
  updateLocationsControl,
} from '../../../../../state/locationsForm';

export type MapFormLocationProps = {
  locationForm: LocationFormType;
  onEndAction: () => void;
  goBack?: () => void;
};

function useMapFormLocation({ locationForm, onEndAction }: MapFormLocationProps) {
  const [locationsControl, setLocationsControl] = useRecoilState(locationsControlState);
  const currentLocation = locationsControl.find((location) => isEqualLocations(location, locationForm));
  const isAddedLocation = locationForm.type === TypeLocation.New && !currentLocation;
  const form = useForm<LocationEditableFields>({
    defaultValues: currentLocation || DEFAULT_LOCATION,
    mode: 'onChange',
  });
  const {
    handleSubmit,
    control,
    formState: { isValid },
  } = form;
  const onFormSubmit = useCallback(
    (locationFields: LocationEditableFields) => {
      setLocationsControl((prevLocationsControl) => {
        if (isAddedLocation) {
          return [
            ...prevLocationsControl,
            {
              ...locationForm,
              ...locationFields,
              canRemove: true,
            },
          ];
        }
        return updateLocationsControl(prevLocationsControl, locationForm, (prevLocation) => ({
          ...prevLocation,
          ...locationFields,
        }));
      });
      onEndAction();
    },
    [onEndAction, setLocationsControl, locationForm, isAddedLocation],
  );
  const onDelete = useCallback(() => {
    setLocationsControl((prevLocationsControl) => {
      if (locationForm.type === TypeLocation.New) {
        return prevLocationsControl.filter((location) => !isEqualLocations<LocationFormType>(location, locationForm));
      }
      return updateLocationsControl(
        prevLocationsControl,
        locationForm,
        (item) =>
          ({
            ...item,
            type: TypeLocation.Delete,
          } as LocationControlItem),
      );
    });
    onEndAction();
  }, [setLocationsControl, locationForm, onEndAction]);

  const validateTitle = (value: string): boolean => {
    const normalised = value.trim().toLowerCase();
    return !locationsControl
      .filter((l) => l.type !== TypeLocation.Delete)
      .filter((l) => !isEqualLocations(l, locationForm))
      .some((l) => l.title.trim().toLowerCase() === normalised);
  };

  return {
    onDelete,
    onSubmit: handleSubmit(onFormSubmit),
    isAddedLocation,
    control,
    form,
    validateTitle,
    isValid,
  };
}

export default useMapFormLocation;
